import type {
  AwardRegister,
  AwardTypeCode,
  InsightsOverview,
  LibraryReport,
  LibraryReportId,
  Me,
  RegisterApproval,
  RegisterQuery,
  RegisterRow,
  RegisterStage,
  RegisterStep,
  ReportScope,
  SpotAwardRow,
  ResolvedPeriod,
} from '@myrewards/contracts';
import { lastMonthsWindow, SMALL_GROUP_WORDS, sinceWords } from '@myrewards/domain';
import { formatDate, formatMoney, formatMonth, type IconName, type StatusTone } from '../ui';
import { fiscalYearDates, fiscalYearOn } from '../ui/fiscal';
import { canOpenArea, holds } from '../settings/nav';

// Words Insights shows. Plain stages and steps only: never an internal state code, never a split
// fraction (CLAUDE.md hard rules).

export const TYPE_NAME: Record<AwardTypeCode, string> = {
  SDB: 'Sector Discretionary Bonus',
  EDB: 'Executive Discretionary Bonus',
  PAA: 'Professional Achievement Award',
  TAA: 'Team Achievement Award',
};

/** Short names for tables ("Sector Discretionary", "PAA"). */
export const TYPE_SHORT: Record<AwardTypeCode, string> = {
  SDB: 'Sector Discretionary',
  EDB: 'Executive Discretionary',
  PAA: 'PAA',
  TAA: 'TAA',
};

/** The Insights sections in their fixed order, by the report that opens each. */
export const SECTIONS = [
  { id: 'overview', label: 'Overview', href: '/insights', report: null },
  {
    id: 'team_recognition',
    label: 'Team recognition',
    href: '/insights/team',
    report: 'team_recognition',
  },
  { id: 'all_awards', label: 'All awards', href: '/insights/awards', report: 'all_awards' },
  { id: 'heat_map', label: 'Heat map', href: '/insights/heat-map', report: 'heat_map' },
  {
    id: 'budgets_history',
    label: 'Budgets & history',
    href: '/insights/budgets',
    report: 'budgets_history',
  },
  { id: 'spend', label: 'Spend', href: '/insights/spend', report: 'spend' },
  { id: 'spot_awards', label: 'Spot awards', href: '/insights/spot', report: 'spot_awards' },
  // Last, as on the boards, and only while it's switched on for this reader (QA-026, A35).
  {
    id: 'ask_data',
    label: 'Ask the data',
    href: '/insights/ask',
    report: 'ask_data',
    icon: 'sparkle',
    whenOn: true,
  },
] as const satisfies readonly {
  id: string;
  label: string;
  href: string;
  report: LibraryReportId | null;
  /** A decorative icon before the label. */
  icon?: IconName;
  /** Shown only while the report is available (switched on), not merely listed. */
  whenOn?: boolean;
}[];

export type SectionId = (typeof SECTIONS)[number]['id'];

/**
 * NAV1 (APP-045 A47): when a person's Insights reaches only their own sectors (a Sector Lead with
 * no company-wide role), it is the board's three tabs and lands on Team recognition. The heat map
 * and Budgets & history still open, from Search & jump and the links on these pages, and keep the
 * tab they belong to lit. Roles blend: anyone with a company-wide role gets the whole set.
 */
export const SECTOR_SECTIONS = [
  {
    id: 'team_recognition',
    label: 'Team recognition',
    href: '/insights/team',
    report: 'team_recognition',
    also: ['/insights/heat-map'],
  },
  { id: 'all_awards', label: 'Award register', href: '/insights/awards', report: 'all_awards' },
  {
    id: 'spend',
    label: 'Budget & spend',
    href: '/insights/spend',
    report: 'spend',
    also: ['/insights/budgets'],
  },
  // APP-191 D16: a Sector Lead asks about their own sectors, while Ask the data is switched on.
  {
    id: 'ask_data',
    label: 'Ask the data',
    href: '/insights/ask',
    report: 'ask_data',
    icon: 'sparkle',
    whenOn: true,
  },
] as const satisfies readonly {
  id: SectionId;
  label: string;
  href: string;
  report: LibraryReportId;
  also?: readonly string[];
  icon?: IconName;
  /** Shown only while the report is available (switched on), not merely listed. */
  whenOn?: boolean;
}[];

/** Whether this reader's Insights is their own sectors only (NAV1's set, A47). */
export function sectorsOnly(scope: ReportScope): boolean {
  return scope.kind === 'sectors';
}

/** Where a sectors-only reader's Insights opens: the first of their tabs they can open. */
export function sectorLanding(library: readonly LibraryReport[]): string {
  return SECTOR_SECTIONS.find((x) => canOpen(library, x.report))?.href ?? '/insights/awards';
}

/** Whether the reader may open a report, from the library the server built for them. */
export function canOpen(library: readonly LibraryReport[], id: LibraryReportId): boolean {
  return library.some((r) => r.id === id);
}

/** Who a report covers, as the start of a scoped label: "Company", "Maritime Airborne". */
export function scopeName(scope: ReportScope): string {
  return scope.kind === 'company' ? 'Company' : scope.sectors.map((s) => s.name).join(' and ');
}

/** The scope in running copy: "the whole company", "Maritime Airborne". */
export function scopeWords(scope: ReportScope): string {
  return scope.kind === 'company' ? 'the whole company' : scopeName(scope);
}

/**
 * "2026 so far", "2025", "Last 12 months · since Nov 1, 2025", "Oct 1 – Oct 14, 2026", "Any time".
 * The last 12 months say where they start (APP-199).
 */
export function periodLabel(period: ResolvedPeriod, asOf: string): string {
  switch (period.kind) {
    case 'fiscal_year': {
      // Fiscal years from the organization's start (APP-018).
      const year = period.fiscalYear ?? fiscalYearOn(asOf);
      return year === fiscalYearOn(asOf) ? `${String(year)} so far` : String(year);
    }
    case 'last_12_months':
      return period.from === null ? 'Last 12 months' : lastTwelveLabel(period.from);
    case 'any_time':
      return 'Any time';
    case 'range':
      return period.from !== null && period.to !== null
        ? `${formatDate(period.from)} – ${formatDate(period.to)}`
        : 'Custom range';
  }
}

/**
 * "Jan 1 – Oct 14, 2026" for a year to date, or the whole year once it has ended (or before it
 * starts). A fiscal year that crosses New Year keeps both years: "Jul 1, 2026 – Mar 15, 2027".
 */
export function yearSpan(year: number, asOf: string): string {
  const { from, to } = fiscalYearDates(year);
  const end = from <= asOf && asOf <= to ? asOf : to;
  const first = formatDate(from);
  const start =
    from.slice(0, 4) === end.slice(0, 4) ? first.replace(`, ${from.slice(0, 4)}`, '') : first;
  return `${start} – ${formatDate(end)}`;
}

/**
 * A year picker's words: "2026 so far" or "2025". When the fiscal year doesn't start on January 1
 * the dates follow, so a year that crosses New Year reads as it runs: "2026 so far · Jul 1, 2026 –
 * Mar 15, 2027" (APP-018: screens show the dates next to the year).
 */
export function yearOption(year: number, asOf: string): string {
  const words = year === fiscalYearOn(asOf) ? `${String(year)} so far` : String(year);
  return fiscalYearDates(year).from.endsWith('-01-01')
    ? words
    : `${words} · ${yearSpan(year, asOf)}`;
}

/**
 * "Nov 2025 – Oct 2026": the last 12 months' calendar months as of the date (the domain's one rule,
 * APP-199). For the Period picker's 12-month choice while a fiscal year is shown (the response's
 * window is then the year's).
 */
export function rollingSpan(asOf: string, months = 12): string {
  return monthSpan(lastMonthsWindow(asOf, months).months);
}

/**
 * "last 12 months · since Nov 1, 2025" (APP-199): the last 12 months in a scope line, saying where
 * they start, from the window the report counted (its `from`) or, before one comes back, today's.
 */
export function lastTwelveWords(from: string): string {
  return `last 12 months · ${sinceWords(from)}`;
}

/** "Last 12 months · since Nov 1, 2025": as `lastTwelveWords`, to start a line or an option. */
export function lastTwelveLabel(from: string): string {
  return `Last 12 months · ${sinceWords(from)}`;
}

/**
 * "12 months · since Nov 1, 2025": the repeat rule's months ending with a window's last day, as the
 * server counts the Repeat flag (whole calendar months, APP-199).
 */
export function repeatMonthsWords(months: number, to: string): string {
  return `${String(months)} months · ${sinceWords(lastMonthsWindow(to, months).from)}`;
}

/** Where the last 12 months start as of a date: `2025-11-01` on any day of October 2026. */
export function lastTwelveFrom(asOf: string): string {
  return lastMonthsWindow(asOf).from;
}

/**
 * A fiscal year's axis (A50): each month's short name, with its year when the name comes round
 * twice (a mid-month start has two Julys).
 */
export function fiscalAxis(months: readonly string[]): string[] {
  return months.map((month) => {
    const name = formatMonth(month);
    return months.filter((x) => formatMonth(x) === name).length > 1
      ? `${name} ${month.slice(0, 4)}`
      : name;
  });
}

/** "Nov 2025 – Oct 2026" from the first and last months of a window. */
export function monthSpan(months: readonly string[]): string {
  const first = months[0];
  const last = months.at(-1);
  if (first === undefined || last === undefined) return '';
  return `${formatMonth(first)} ${first.slice(0, 4)} – ${formatMonth(last)} ${last.slice(0, 4)}`;
}

/**
 * The date cell of a register row with no date (APP-186), in the words of the server's month
 * groups. A draft sent back with no award date has none until it's sent again; an award that
 * ended that way never will, so it never says "yet".
 */
export function noDateWords(row: Pick<RegisterRow, 'ended'>): string {
  return row.ended === null ? 'No date yet' : 'No award date';
}

/** The month group a row with no date belongs to (the server's keys, APP-186). */
export function noDateGroup(row: Pick<RegisterRow, 'ended'>): string {
  return row.ended === null ? 'none' : 'no_award_date';
}

export const STAGE_LABEL: Record<RegisterStage, string> = {
  draft: 'Draft',
  approval: 'Approval',
  with_stock_admin: 'With the Stock Admin team',
  employee_choice: 'Employee’s choice',
  paid: 'Paid',
  closed: 'Closed',
  ended: 'Rejected or cancelled',
};

/** "A", "A and B", "A, B and C". */
export function andList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1) ?? ''}`;
}

const COST_WORDS: Record<NonNullable<RegisterQuery['costType']>, string> = {
  OH: 'Overhead',
  GA: 'G&A',
};
const LINE_STATUS_WORDS: Record<NonNullable<RegisterQuery['lineStatus']>, string> = {
  sent: 'Sent',
  in_progress: 'In progress',
  paid: 'Paid',
};

/**
 * All awards' filters in words, one phrase each, in the order the page lists them ("Maritime
 * Airborne", "PAA and TAA", "For Jordan Ellis"); null for one the page can't name. Empty without
 * filters: the time frame isn't one (QA-035, APP-045 A37).
 */
export function filterWords(register: AwardRegister, view: RegisterQuery): (string | null)[] {
  const named = (list: readonly { id: string; name: string }[], id: string) =>
    list.find((x) => x.id === id)?.name ?? null;
  const out: (string | null)[] = [];
  if (view.sectorId !== undefined) out.push(named(register.choices.sectors, view.sectorId));
  if (view.poolId !== undefined) {
    const pool = named(register.choices.pools, view.poolId);
    out.push(pool === null ? null : `${pool} pool`);
  }
  if (view.type !== undefined && view.type.length > 0)
    out.push(andList(view.type.map((t) => TYPE_SHORT[t])));
  if (view.stage !== undefined && view.stage.length > 0)
    out.push(andList(view.stage.map((x) => STAGE_LABEL[x])));
  if (view.programId !== undefined) out.push(named(register.choices.programs, view.programId));
  if (view.personId !== undefined) {
    const person = register.person;
    out.push(person?.id === view.personId ? `For ${person.name}` : null);
  }
  if (view.enteredById !== undefined) {
    const by = named(register.choices.enteredBy, view.enteredById);
    out.push(by === null ? null : `Entered by ${by}`);
  }
  if (view.costType !== undefined) out.push(COST_WORDS[view.costType]);
  if (view.q !== undefined) out.push(`Matching “${view.q}”`);
  if (view.lineStatus !== undefined) out.push(LINE_STATUS_WORDS[view.lineStatus]);
  return out;
}

/**
 * What the hero calls a filtered view: its one filter, named, or "This view" when there are
 * several (or one it can't name); null without filters (QA-035, APP-045 A37).
 */
export function viewName(register: AwardRegister, view: RegisterQuery): string | null {
  const words = filterWords(register, view);
  if (words.length === 0) return register.filtersOn ? 'This view' : null;
  const [only] = words;
  return words.length === 1 && only !== null && only !== undefined ? only : 'This view';
}

/**
 * A sector reader's discretionary pools by name ("Maritime Airborne pool"; two read "… and …
 * pools"): their awards come from their own sector's pool, not "the discretionary pools". Null for
 * the company view, or when no pool of theirs is shown (QA-035, APP-045 A39).
 */
export function ownPools(overview: InsightsOverview): string | null {
  if (overview.scope.kind === 'company') return null;
  const own = overview.pools.filter((p) =>
    p.kind === undefined ? p.pool.id !== overview.paaTaaPoolId : p.kind === 'sector',
  );
  const names = own.map((p) => p.pool.name.replace(/ pool$/i, ''));
  if (names.length === 0) return null;
  return `${andList(names)} ${names.length === 1 ? 'pool' : 'pools'}`;
}

/** A time frame inside a label: "last 12 months", "any time"; years and dates as they are. */
export function frameInLine(frame: string): string {
  return /^(Last|Any|Custom)\b/.test(frame)
    ? `${frame.charAt(0).toLowerCase()}${frame.slice(1)}`
    : frame;
}

/** "Last 12 months" without filters; "Maritime Airborne · last 12 months" with one. */
export function viewFrame(name: string | null, frame: string): string {
  return name === null ? frame : `${name} · ${frameInLine(frame)}`;
}

const STEP_WORDS: Record<RegisterStep, string> = {
  waiting_for_stock_admin: 'waiting to be claimed',
  checking: 'checking',
  letters: 'letters',
  released: 'letters released',
  past_deadline: 'past deadline',
  with_payroll: 'with Payroll',
  statement_check: 'earning statement check',
  shareworks: 'Shareworks',
};

/**
 * Where a Spot award is (RPT4). APP-247 (change 17): its letter is the Stock Admin team's to check
 * and release again (APP-244), so that stage reads as theirs, as every award's does; before, the
 * committee's (APP-049, APP-045 A41). The export says the same.
 */
export function spotWhereItIs(row: Pick<SpotAwardRow, 'stage' | 'step'>): {
  label: string;
  tone: StatusTone;
} {
  switch (row.stage) {
    case 'with_stock_admin':
      return {
        label:
          row.step === 'letters'
            ? 'With the Stock Admin team · letter to check'
            : row.step === 'released'
              ? 'With the Stock Admin team · letter released'
              : 'With the Stock Admin team',
        tone: 'info',
      };
    case 'paid':
      return row.step === 'with_payroll'
        ? { label: 'With Payroll', tone: 'info' }
        : {
            label: row.step === 'statement_check' ? 'Paid · statement check' : 'Paid',
            tone: 'done',
          };
    case 'closed':
      return { label: 'Closed', tone: 'done' };
    case null:
      return { label: 'Imported · old tracker', tone: 'neutral' };
    default:
      return { label: 'In progress', tone: 'waiting' };
  }
}

/** "Where it is": the plain stage, its step, and a tone that always comes with the words. */
export function whereItIs(row: Pick<RegisterRow, 'stage' | 'step' | 'ended' | 'approval'>): {
  label: string;
  tone: StatusTone;
} {
  if (row.ended !== null)
    return { label: row.ended === 'rejected' ? 'Rejected' : 'Cancelled', tone: 'neutral' };
  switch (row.stage) {
    case 'draft':
      return { label: 'Draft · returned to whoever entered it', tone: 'neutral' };
    case 'approval': {
      const a = row.approval;
      if (a.status !== 'waiting') return { label: 'Approval', tone: 'waiting' };
      if (a.overBudget) return { label: 'Approval · over budget', tone: 'problem' };
      return {
        label:
          a.on === 'sector_lead'
            ? 'Approval · Sector Lead'
            : a.on === 'named_executive'
              ? 'Approval · Executive'
              : a.on === 'executives_outside_app'
                ? 'Approval · Executives decide'
                : 'Approval · Executive decision',
        tone: 'waiting',
      };
    }
    case 'with_stock_admin':
      return {
        label:
          row.step === null
            ? STAGE_LABEL.with_stock_admin
            : `${STAGE_LABEL.with_stock_admin} · ${STEP_WORDS[row.step]}`,
        tone: 'info',
      };
    case 'employee_choice':
      return row.step === 'past_deadline'
        ? { label: 'Employee’s choice · past deadline', tone: 'problem' }
        : { label: STAGE_LABEL.employee_choice, tone: 'waiting' };
    case 'paid':
      return {
        label:
          row.step === null || row.step === 'released' ? 'Paid' : `Paid · ${STEP_WORDS[row.step]}`,
        tone: 'done',
      };
    case 'closed':
      return { label: 'Closed', tone: 'neutral' };
    case null:
      return { label: 'Closed', tone: 'neutral' };
  }
}

/** "Approved by": who approved it, who it waits for, or why it didn't need anyone. */
export function approvalWords(a: RegisterApproval): { label: string; note: string | null } {
  switch (a.status) {
    case 'not_needed':
      return { label: 'Not needed', note: null };
    case 'waiting':
      if (a.on === 'sector_lead')
        return {
          label: a.person === null ? 'Waiting · the Sector Lead' : `Waiting · ${a.person.name}`,
          note: null,
        };
      if (a.on === 'named_executive')
        return {
          label: a.person === null ? 'Waiting · the Executive' : `Waiting · ${a.person.name}`,
          note: null,
        };
      if (a.on === 'executives_outside_app')
        return { label: 'Waiting · Executives decide', note: 'outside the app' };
      return { label: 'Waiting · any one Executive', note: a.overBudget ? 'over budget' : null };
    case 'approved': {
      const notes = [
        a.outsideApp ? 'outside the app' : null,
        a.overBudget ? 'over budget' : null,
      ].filter((n): n is string => n !== null);
      const who = a.by?.name ?? (a.outsideApp ? 'Executives' : 'Approved');
      // APP-148 R140: approved by a Stock Admin for someone, it says for whom.
      const label = a.by != null && a.for != null ? `${who} for ${a.for.name}` : who;
      return { label, note: notes.length > 0 ? notes.join(' · ') : null };
    }
  }
}

/** "$10,000 + 64 options" for a total; amounts only, never a split. */
export function moneyAndOptions(cashCents: number, options: number): string {
  if (options === 0) return formatMoney(cashCents);
  return `${formatMoney(cashCents)} + ${options.toLocaleString('en-US')} options`;
}

/** Whole dollars, short: "$46K", "$126.8K", "$1.2M". For axis ticks and chart labels. */
export function shortMoney(cents: number): string {
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  const sign = dollars < 0 ? '−' : '';
  const trim = (n: number) => (Math.round(n * 10) / 10).toString();
  if (abs >= 1_000_000) return `${sign}$${trim(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}$${trim(abs / 1_000)}K`;
  return `${sign}$${String(Math.round(abs))}`;
}

/** "27%", or the counts for a group too small for a percent ("2 of 3"). */
export function shareWords(recognized: number, people: number, percent: number | null): string {
  return percent === null
    ? `${recognized.toLocaleString('en-US')} of ${people.toLocaleString('en-US')}`
    : `${String(percent)}%`;
}

/**
 * Why a chart draws no bar for these groups (APP-197): each has fewer than SMALL_GROUP people, so
 * a rate chart shows none ("Space Systems has fewer than 5 people, so it has no rate or bar.") and
 * a share chart shows its counts. Empty when every group is big enough.
 */
export function smallGroupNote(names: readonly string[], measure: 'rate' | 'percent'): string {
  if (names.length === 0) return '';
  const one = names.length === 1;
  const fewer = SMALL_GROUP_WORDS.toLowerCase();
  return measure === 'rate'
    ? `${andList(names)} ${one ? `has ${fewer}, so it has` : `have ${fewer} each, so they have`} no rate or bar.`
    : `${andList(names)} ${one ? `has ${fewer}, so it shows` : `have ${fewer} each, so they show`} counts, not a percent or bar.`;
}

/**
 * Where this person sets a pool's budget, if they can (the rule Waiting on you uses): Executives on
 * Executive › Budget pools, Finance and System Admins on Settings › Budgets. Stock Admins and
 * everyone else only read budgets, so there's no link for them.
 */
export function budgetSetting(me: Pick<Me, 'roles'>): { href: string; where: string } | null {
  if (holds(me, 'executive'))
    return { href: '/manage/executive/budget-pools', where: 'Executive › Budget pools' };
  if (holds(me, 'finance', 'system_admin') && canOpenArea(me, 'budgets'))
    return { href: '/settings/budgets', where: 'Settings › Budgets' };
  return null;
}

/** A nominal month on an empty money-by-month chart: $50,000, unless a budget gives a real one. */
export const NOMINAL_MONTH_CENTS = 5_000_000;

/** An even month's share of a year's budget, for an empty chart's axis; else the nominal month. */
export function monthlyShare(budgetCents: number | null | undefined, months = 12): number {
  return budgetCents !== null && budgetCents !== undefined && budgetCents > 0
    ? budgetCents / Math.max(1, months)
    : NOMINAL_MONTH_CENTS;
}
